import type { JSX } from 'react'
/**
 * Client-side authorization state.
 *
 * Permissions come from `/auth/me`, never from decoding the token — the token
 * carries none, deliberately, so decoding it would tell the client nothing.
 *
 * All of this is user experience only. The backend validates permission, scope
 * and policy on every request regardless of what the UI chose to render.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { MeResponse } from '@erp/contracts'

interface AuthState {
  me: MeResponse | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
  logout: () => Promise<void>
}

const AuthCtx = createContext<AuthState | null>(null)

async function fetchMe(): Promise<MeResponse | null> {
  const response = await fetch('/api/auth/me', { credentials: 'include' })

  if (response.status === 401) {
    // The access token is short-lived; one silent refresh before giving up.
    const refreshed = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
    if (!refreshed.ok) return null

    const retry = await fetch('/api/auth/me', { credentials: 'include' })
    if (!retry.ok) return null
    return (await retry.json()) as MeResponse
  }

  if (!response.ok) return null
  return (await response.json()) as MeResponse
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setMe(await fetchMe())
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not reach the server')
      setMe(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const logout = useCallback(async () => {
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
    const body = (await response.json().catch(() => ({}))) as { endSessionUrl?: string | null }
    setMe(null)

    // Revoking only the local session leaves the provider's session live, so
    // "log in" would sign straight back in with no prompt.
    window.location.href = body.endSessionUrl ?? '/login'
  }, [])

  const value = useMemo<AuthState>(
    () => ({ me, loading, error, reload, logout }),
    [me, loading, error, reload, logout],
  )

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthCtx)
  if (context === null) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}

/** `can('crm.lead.create')` — the only permission check components should make. */
export function useCan(): (permission: string) => boolean {
  const { me } = useAuth()
  const permissions = useMemo(() => new Set(me?.permissions ?? []), [me])
  return useCallback((permission: string) => permissions.has(permission), [permissions])
}

export function useCanAny(): (...permissions: string[]) => boolean {
  const can = useCan()
  return useCallback((...permissions: string[]) => permissions.some(can), [can])
}

/**
 * Conditional rendering by permission.
 *
 *   <Can permission="crm.lead.create"><NewLeadButton /></Can>
 */
export function Can({
  permission,
  any,
  fallback = null,
  children,
}: {
  permission?: string
  any?: string[]
  fallback?: ReactNode
  children: ReactNode
}): JSX.Element {
  const can = useCan()
  const allowed =
    (permission !== undefined && can(permission)) ||
    (any !== undefined && any.some(can))

  return <>{allowed ? children : fallback}</>
}